import {
  useOnUpdateSourcedCandidateSubscription,
  useOnCreateSourcedCandidateSubscription,
  useSearchSourcingCandidatesQuery
} from 'generated-graphql/hooks/apollo-hooks';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';
import { updateApolloListById } from 'core/utils/apollo/updateApolloListById';
import {
  SearchableSourcedCandidateFilterInput,
  SearchableSourcedCandidateSortInput
} from 'generated-graphql/graphql';

type Props = {
  filter: SearchableSourcedCandidateFilterInput;
  candidateBatchId?: string;
  limit?: number;
  from?: number;
  sort?: SearchableSourcedCandidateSortInput[];
};

export const useSearchSourcingCandidatesWithSubscription = ({
  filter,
  candidateBatchId,
  limit,
  from,
  sort
}: Props) => {
  const { data, loading, updateQuery } = useSearchSourcingCandidatesQuery({
    variables: {
      filter,
      limit,
      from,
      sort
    }
  });

  const { error: updateError } = useOnUpdateSourcedCandidateSubscription({
    variables: candidateBatchId
      ? {
          filter: {
            candidateBatchId: { eq: candidateBatchId }
          }
        }
      : undefined,
    onData: ({ data: { data } }) => {
      const candidateUpdated = data?.onUpdateSourcedCandidate;

      if (!candidateUpdated) return;

      updateQuery((prevData) =>
        updateApolloListById({
          prevData,
          updatedItem: candidateUpdated,
          idKey: 'id'
        })
      );
    }
  });

  const { error: createError } = useOnCreateSourcedCandidateSubscription({
    variables: candidateBatchId
      ? {
          filter: {
            candidateBatchId: { eq: candidateBatchId }
          }
        }
      : undefined,
    onData: ({ data: { data } }) => {
      const candidateCreated = data?.onCreateSourcedCandidate;

      if (!candidateCreated) return;

      updateQuery((prevData) =>
        updateApolloListById({
          prevData,
          updatedItem: candidateCreated,
          idKey: 'id'
        })
      );
    }
  });

  const sourcedCandidates = extractQueryListItems(data);
  const total = data?.searchSourcedCandidates?.total ?? 0;

  return {
    sourcedCandidates,
    total,
    loading,
    subscriptionError: createError || updateError
  };
};
